/**
 * The onboarding buddy's second job (workspace-client.md §1a): answering the prose command.
 *
 * The residual risk §4 names is that §1a is charming enough to be SKIPPED - and the player who
 * dismisses the buddy DM is exactly the player who types "roll back the deploy" into a channel. So the
 * failure path has to work when the onboarding did not.
 *
 * What happens: the sentence posts as chat, because it IS chat, and the buddy answers with where that
 * move actually lives. This is standards/messages.md - every message names the player's next move -
 * applied to a client with no command line, and it is the same near-miss help the terminal gives when
 * somebody types `/standup`.
 *
 * The detection is deliberately crude and deliberately cheap: no model call, no round trip, and a false
 * positive costs a helpful sentence from a colleague. A false NEGATIVE costs silence, which is the one
 * outcome the section forbids, so the patterns lean generous.
 */
import { t } from './i18n.ts';

/**
 * Phrases that mean "do the thing" rather than "let us talk about the thing". Both languages, because
 * the EN/RU bar applies to the failure paths too (§2j) - a Russian table typing Russian imperatives at
 * a channel and getting nothing back is the same bug in a different alphabet.
 */
const IMPERATIVES: RegExp[] = [
  // English: an imperative or a "let us / can we / should we" wrapped around one.
  /\b(roll ?back|revert|deploy|ship|mitigate|escalate|declare|rollback|restart|failover|hotfix)\b/i,
  /\b(let'?s|can we|should we|shall we|we should|i'?ll)\b.*\b(roll ?back|revert|deploy|ship|fix|mitigate|escalate|restart)\b/i,
  // Russian. NOTE: no `\b` - JavaScript's word boundary is ASCII-only, so `\bоткат` can never match.
  // The stems are distinctive enough on their own, which is the reason this stayed a substring check.
  /(откат|раскат|деплой|выкат|митиг|эскалир|перезапус|чини|почини|фикс|рестарт)/i,
  /(давай|может|нужно|надо|стоит)[\s\S]*(откат|деплой|выкат|почин|чини|фикс|перезапус)/i,
];

/** True when this line reads as an instruction to the system rather than a remark to the room. */
export function readsAsCommand(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return false;
  }

  // A question is a question. "can we roll back?" is still a command in intent - the design's own
  // example is exactly that - so questions are not excluded, only empty lines.
  return IMPERATIVES.some((p) => p.test(trimmed));
}

/**
 * What the buddy says back. Two answers, because the honest one depends on whether a decision is
 * actually waiting: pointing at `#war-room` when `#war-room` holds nothing is the "action that does not
 * work" standards/messages.md §2.4 calls worse than no action at all.
 */
export function buddyAnswer(hasOpenIncident: boolean): string {
  return hasOpenIncident ? t('buddy.proseCommand') : t('buddy.proseCommandIdle');
}

/**
 * The answer to anything else said in the buddy's DM. It is not a parser and does not pretend to be
 * one: free text to characters is step 4, gated on the two C2 seams §4a names. Until then the buddy
 * answers as a person with a known competence - "here is what I can tell you" - which is also simply
 * true of talking to a busy colleague, and never as a parser error (§2c: one visible `unknown-topic:`
 * and the magic is dead for the session).
 */
export function buddyFallback(): string {
  return t('buddy.unknown');
}
